import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import Input from "../../components/common/Input";
import Button from "../../components/common/Button";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { ForgotPasswordFormData } from "../../schemas/auth/formData";
import { forgotPasswordSchema } from "../../schemas/auth/schemas";
import { Link } from "react-router-dom";

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { forgotPassword } = useAuth();

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await forgotPassword(data);
      navigate("/verify-otp", { state: { email: data.email } });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to send OTP");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 shadow-lg rounded bg-white p-10">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-primary-default">
            Forgot your password?
          </h2>
          <p className="mt-2 text-center text-sm text-gray-800">
            Enter your email address and we'll send you an OTP to reset your
            password.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm">
            <Input
              label="Email address"
              type="email"
              name="email"
              value={watch("email") || ""}
              onChange={(e) => setValue("email", e.target.value, { shouldValidate: false })}
              onBlur={() => trigger("email")}
              error={errors.email}
              autoComplete="email"
              required
              className="rounded-t-md"
              placeholder="Email address"
            />
          </div>

          <div>
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Send OTP
            </Button>
          </div>

          <div className="text-sm text-center">
            <Link
              to={"/login"}
              className="font-medium text-primary-default hover:text-primary-light"
            >
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
